'use client';

import noop from 'lodash/noop';
import {
  createContext,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type TreeStateContextValue = {
  getNodeStateById: (nodeId: string) => boolean;
  onNodeChange: (nodeId: string, open: boolean) => void;
};

const defaultValue: TreeStateContextValue = {
  getNodeStateById: () => false,
  onNodeChange: noop,
};

const TreeStateContext = createContext(defaultValue);

export type TreeStateContextProviderProps = PropsWithChildren<{
  /** Node IDs to auto-expand (e.g., selected library/board IDs) */
  autoExpandIds?: string[];
}>;

export const TreeStateContextProvider = memo(({ children, autoExpandIds }: TreeStateContextProviderProps) => {
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});

  // Auto-expand nodes when autoExpandIds changes
  useEffect(() => {
    if (autoExpandIds && autoExpandIds.length > 0) {
      setExpandedNodes((prev) => {
        const newExpanded = { ...prev };
        for (const id of autoExpandIds) {
          if (id) {
            newExpanded[id] = true;
          }
        }
        return newExpanded;
      });
    }
  }, [autoExpandIds]);

  const getNodeStateById = useCallback(
    (nodeId: string) => {
      return expandedNodes[nodeId] ?? false;
    },
    [expandedNodes],
  );

  const onNodeChange = useCallback((nodeId: string, open: boolean) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: open,
    }));
  }, []);

  const value = useMemo(() => ({ getNodeStateById, onNodeChange }), [getNodeStateById, onNodeChange]);

  return <TreeStateContext.Provider value={value}>{children}</TreeStateContext.Provider>;
});

TreeStateContextProvider.displayName = 'TreeStateContextProvider';

export const useTreeStateContext = () => {
  const context = useContext(TreeStateContext);

  if (context === defaultValue) {
    throw new Error('useTreeStateContext must be used within a TreeStateContextProvider');
  }

  return context;
};
